import React, { useState, useRef, useEffect } from "react";
import { FiChevronLeft, FiChevronRight, FiChevronDown } from "react-icons/fi";
import { IconWrapper } from "../../IconWrapper";
import { FloorAssignment } from "./types";
import { useColorMode } from "@chakra-ui/react";
import { toLocalDateString } from "../../../utils/dateUtils";

interface DateSelectorProps {
  selectedDate: Date;
  onDateChange: (date: Date) => void;
  theme: any;
  floorAssignments: FloorAssignment[];
}

export const DateSelector: React.FC<DateSelectorProps> = ({ selectedDate, onDateChange, theme, floorAssignments }) => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const scrollRef = useRef<HTMLDivElement>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState<number>(new Date().getMonth());

  const isInternalChange = useRef(false);

  // Sync internal state if selectedDate changes externally
  useEffect(() => {
    if (isInternalChange.current) {
        isInternalChange.current = false;
        return;
    }
    // Only update if the selectedDate year/month is different from current internal state
    // to avoid loop or unnecessary updates
    if (selectedDate.getFullYear() !== selectedYear || selectedDate.getMonth() !== selectedMonth) {
        setSelectedYear(selectedDate.getFullYear());
        setSelectedMonth(selectedDate.getMonth());
    }
  }, [selectedDate]);
 

  useEffect(() => {
    // Calculate new days for the new month/year
    const daysInNewMonth = [];
    const daysCount = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 1; i <= daysCount; i++) {
        const d = new Date(selectedYear, selectedMonth, i);
        if (d >= today) {
            daysInNewMonth.push(d);
        }
    }

    // Find the first valid date (with assignment)
    const validDate = daysInNewMonth.find(d => hasAssignment(d));
    
    // If we have a valid date, select it. 
    // If not, but we have ANY future date in this month, select the first one.
    // If absolutely no future dates (past month?), select the 1st just to show empty state.
    
    // However, we must ensure we don't trigger this on initial mount if selectedDate matches.
    if (selectedDate.getFullYear() === selectedYear && selectedDate.getMonth() === selectedMonth) {
        return; 
    }

    isInternalChange.current = true; // Mark as internal change

    if (validDate) {
        onDateChange(validDate);
    } else if (daysInNewMonth.length > 0) {
        // No assignment, but valid future date exists -> Select it so user sees "No floors"
        onDateChange(daysInNewMonth[0]);
    } else {
         // No future dates at all (e.g. user went back to past month, or month is over)
         // Select the last day of month? Or 1st?
         // Let's select the 1st to be safe, parent will likely show empty or filter out past.
         // Actually if filter excludes past, parent might show nothing.
         const firstOfMonth = new Date(selectedYear, selectedMonth, 1);
         onDateChange(firstOfMonth);
    }
  }, [selectedYear, selectedMonth]);

  const scroll = (direction: 'left' | 'right') => {
    if (scrollRef.current) {
      const scrollAmount = 300;
      scrollRef.current.scrollBy({ left: direction === 'left' ? -scrollAmount : scrollAmount, behavior: 'smooth' });
    }
  };

    const hasAssignment = (date: Date) => {
    // For demo/visual purposes, we might want to allow clicking any date or assume availability if checking strictly is preventing UI check.
    // Keeping logic but ensuring styling works even if disabled.
    const dateStr = toLocalDateString(date);
    return floorAssignments.some(a => a.date === dateStr && a.canView && a.availableSeats > 0);
  };

  // Get dates for selected month/year
  const getDatesForMonth = () => {
    const days = [];
    const daysInMonth = new Date(selectedYear, selectedMonth + 1, 0).getDate();
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    for (let i = 1; i <= daysInMonth; i++) {
      const date = new Date(selectedYear, selectedMonth, i);
      if (date >= today) {
        days.push(date);
      }
    }
    return days;
  };

  const days = getDatesForMonth();
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth();
  const years = Array.from({ length: 5 }, (_, i) => currentYear + i);
  
  const allMonths = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Filter months if current year selected
  const availableMonths = selectedYear === currentYear 
    ? allMonths.map((m, i) => ({ name: m, index: i })).filter(m => m.index >= currentMonth)
    : allMonths.map((m, i) => ({ name: m, index: i }));

  return (
    <div className={`w-full mb-4 p-1`}>
      {/* Year and Month Dropdowns */}
      <div className="flex items-center gap-3 mb-4">
        <div className="relative">
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              className={`
                appearance-none pl-5 pr-8 py-2 rounded-lg font-bold text-xs cursor-pointer outline-none transition-all
                ${isDark 
                    ? 'bg-[#1e2126] text-white border border-[#2E2F33] hover:border-gray-600' 
                    : 'bg-white text-gray-700 border border-gray-100 shadow-sm hover:border-gray-200'}
              `}
            >
              {years.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <IconWrapper icon={FiChevronDown} size={14} className={isDark ? 'text-gray-400' : 'text-gray-400'} />
            </div>
        </div>

        <div className="relative">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
              className={`
                appearance-none pl-3 pr-8 py-2 rounded-lg font-bold text-xs cursor-pointer outline-none transition-all min-w-[120px]
                ${isDark 
                    ? 'bg-[#1e2126] text-white border border-[#2E2F33] hover:border-gray-600' 
                    : 'bg-white text-gray-700 border border-gray-100 shadow-sm hover:border-gray-200'}
              `}
            >
              {availableMonths.map((month) => (
                <option key={month.index} value={month.index}>{month.name}</option>
              ))}
            </select>
            <div className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none">
                <IconWrapper icon={FiChevronDown} size={14} className={isDark ? 'text-gray-400' : 'text-gray-400'} />
            </div>
        </div>
      </div>

      {/* Date Cards Scroller */}
      <div className="relative group">
       
        <button 
          onClick={() => scroll('left')} 
          className={`
            absolute left-0 top-1/2 -translate-y-1/2 -ml-5 z-10 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all
            ${isDark ? 'bg-[#2A3441] text-white' : 'bg-white text-gray-600 border border-gray-100'}
          `}
        >
          <IconWrapper icon={FiChevronLeft} size={16} />
        </button>
 
        <button 
          onClick={() => scroll('right')} 
          className={`
            absolute right-0 top-1/2 -translate-y-1/2 -mr-6 z-10 p-1.5 rounded-full shadow-md opacity-0 group-hover:opacity-100 transition-all
            ${isDark ? 'bg-[#2A3441] text-white' : 'bg-white text-gray-600 border border-gray-100'}
          `}
        >
          <IconWrapper icon={FiChevronRight} size={16} />
        </button>

        {/* Scroll Area */}
        <div 
          ref={scrollRef} 
          className="flex gap-2 overflow-x-auto pb-2  scrollbar-hide p-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {days.length === 0 ? (
             <div className="text-xs text-gray-400 italic py-2">No future dates in this month.</div>
          ) : days.map((date, idx) => {
            const dateStr = toLocalDateString(date);
            const isSelected = toLocalDateString(selectedDate) === dateStr;
            const hasFloorAssignment = hasAssignment(date);
            
    
            
            const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });

            return (
              <button
                key={idx}
                onClick={() => onDateChange(date)}
                className={`
                  flex-shrink-0 flex flex-col items-center justify-center w-[80px] h-[60px] rounded-xl border transition-all duration-200
                  ${isSelected
                    ? isDark 
                        ? 'border-blue-500 bg-blue-500/10 text-blue-400'
                        : 'border-blue-500 bg-blue-50 text-blue-600 shadow-sm'
                    : isDark
                        ? 'bg-[#1e2126] border-[#2E2F33] text-gray-400 hover:border-gray-600'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                  }
                  ${!hasFloorAssignment ? 'opacity-60' : 'hover:scale-105'}
                `}
              >
                <span className={`text-[8px] font-bold mb-0.5 uppercase  ${isSelected ? 'opacity-100' : 'opacity-60'}`}>
                    {dayName}
                </span>
                <span className={`text-lg font-black font-semibold ${isSelected ? 'scale-110' : ''}`}>
                    {date.getDate()}
                </span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};
